import React from 'react';
import { Link as NavLink } from 'react-router-dom';
import { Button, Grid, TextField, Typography, Checkbox, FormControlLabel, Link } from '@mui/material';
import FormContainer from 'containers/FormContainer';
import IntlMessages from 'lngProvider/IntlMessages';
import * as Yup from 'yup';
import PasswordHelperText from 'components/PasswordHelperText';
import GoogleLogo from 'components/svgs/GoogleLogo';
import FacebookLogo from 'components/svgs/FacebookLogo';
import MicrosoftLogo from 'components/svgs/MicrosoftLogo';
import AppleLogo from 'components/svgs/AppleLogo';
import { EmailValidation } from 'services/EnumConstants'
import { styled } from '@mui/material/styles';
import { colors, spacing } from 'styles/Common';

const StyledGrid = styled(Grid)((
    {
        theme
    }
    ) => {
        return {
            maxWidth: '327px',
            marginTop: spacing[5],
            '& a': {
                color: theme.palette.primary.lighter
            },
            '& .MuiInputBase-input': {
                height: 30
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: 48
            },
            '& .MuiButton-root': {
                borderRadius: '8px'
            },
            '& .MuiButton-contained.Mui-disabled': {
                backgroundColor: theme.palette.primary.light
            },
            '& .title-text': {
                fontWeight: 600,
                marginBottom: spacing[2]
            },
            '& .subtitle-text': {
                marginBottom: spacing[2],
                color: colors.fillWhite
            },
            '& .social-button': {
                backgroundColor: colors.fillWhite,
                height: 53,
                width: 68,
                borderWidth: '1px',
                borderColor: theme.palette.secondary.main
            },
            '& .register-button': {
                color: colors.fillWhite,
                marginTop: spacing[3],
                marginBottom: spacing[2],
                height: 48,
                '& .MuiButton-contained.Mui-disabled': {
                    backgroundColor: theme.palette.primary.light
                }
            },
            '& .login-text': {
                color: theme.palette.secondary.light
            },
            '& .register-form-checkbox': {
                paddingTop: "0px",
                paddingBottom: "0px",
                height: 28
            },
            '& .register-checkbox': {
                color: theme.palette.secondary.lighter
            }
        };
});

const RegisterWeb = (props) => {
    const { data, onSubmit, onEmailChanged, onEmailBlurred, onHandleGoogle, onHandleFacebook, onHandleMicrosoft, onHandleApple, registerConfig, onShowTerms, intl } = props;
    const emailRegex = registerConfig.environment === 'Prod' ? EmailValidation.Production : EmailValidation.Lower;
    const signInMessage = props.intl.formatMessage({ id: registerConfig.signInText }).replace(`{loginUrl}`, registerConfig.loginUrl);

    const validationSchema = Yup.object().shape({
        emailFromSocial: Yup.boolean(),
        email: Yup.string()
            .trim()
            .required(true)
            .email(true)
            .matches(emailRegex, true)
            .test('sso', 'register.emailAddressAlreadyExists', (value) => {
                if (value) {
                    // Check for an SSO-supported email
                    const lowerEmail = value.toLowerCase();
                    let isSso = false;
                    if (data.sso) {
                        isSso = data.sso.find(ss => lowerEmail.endsWith(ss.emailDomain));
                    }

                    return !isSso;
                }

                return true;
            }),
        newPassword: Yup.string()
            .when('emailFromSocial', {
                is: false,
                then: (schema) => {
                    return schema
                        .required(<IntlMessages id="register.invalidPassword" />)
                        .min(8, <IntlMessages id="register.invalidPassword" />)
                        .matches(process.env.REACT_APP_PASSWORD_REGEX, true)
                }
            }),
        confirmNewPassword: Yup.string()
            .when('emailFromSocial', {
                is: false,
                then: (schema) => {
                    return schema
                        .required(<IntlMessages id="register.invalidConfirmPassword" />)
                        .oneOf([Yup.ref('newPassword')], true)
                }
            }),
        firstName: Yup.string()
            .required(<IntlMessages id="register.firstNameRequired" />),
        lastName: Yup.string()
            .required(<IntlMessages id="register.lastNameRequired" />)
    });

    const getEmailHelperText = (errors, values) => {

        // external email errors pass messages in the values.emailErrorId, everything else goes through error
        const emailError = values.emailErrorId || errors.email;

        if (emailError) {
            if (emailError === true) {
                // these are the "normal" errors returned by yup
                return (<IntlMessages id="register.invalidEmail" />);
            }
            else if (emailError === 'register.emailAddressAlreadyExists') {
                // user already exists errors
                var loginLink = `/app/login?ReturnUrl=${data.returnUrl}`;
                return (<span><IntlMessages id={emailError} /> <NavLink to={loginLink}><IntlMessages id="register.login" /></NavLink></span>);
            }
            else {
                // all other custom email errors
                return (<IntlMessages id={emailError} />);
            }
        }

        return '';
    };

    return (
        <FormContainer
            initialValues={data}
            validationSchema={validationSchema}
            onSubmit={onSubmit}>

            {props => {
                const {
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    handleChange,
                    handleBlur
                } = props;


                const emailBlurred = (e) => {
                    handleBlur(e);
                    onEmailBlurred(e.target.value, props, values);
                };

                const emailChanged = (e) => {
                    handleChange(e);
                    onEmailChanged(e.target.value, props, values);
                };

                return (
                    <div>

                        <StyledGrid container>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Grid container justifyContent="center">
                                        <Typography variant="h4" className="title-text"><IntlMessages id={registerConfig.registerTitle} /></Typography>
                                    </Grid>
                                </Grid>
                                {values.registerSubTitle ?
                                    <Grid item xs={12}>
                                        <Typography variant="body2" className="subtitle-text"><IntlMessages id={registerConfig.registerSubTitle} /></Typography>
                                    </Grid> : ''}
                                {registerConfig.allowRegisterWithGoogle === true ?
                                    <Grid item xs={3}>
                                        <Button
                                            variant="outlined"
                                            disableElevation={true}
                                            onClick={onHandleGoogle}
                                            className="social-button">
                                            <GoogleLogo />
                                        </Button>
                                    </Grid>
                                    : ''}
                                {registerConfig.allowRegisterWithApple === true ?
                                    <Grid item xs={3}>
                                        <Button
                                            variant="outlined"
                                            disableElevation={true}
                                            onClick={onHandleApple}
                                            className="social-button">
                                            <AppleLogo />
                                        </Button>
                                    </Grid>
                                    : ''}
                                {registerConfig.allowRegisterWithFacebook === true ?
                                    <Grid item xs={3}>
                                        <Button
                                            variant="outlined"
                                            disableElevation={true}
                                            onClick={onHandleFacebook}
                                            className="social-button">
                                            <FacebookLogo />
                                        </Button>
                                    </Grid>
                                    : ''}
                                {registerConfig.allowRegisterWithMicrosoft === true ?
                                    <Grid item xs={3}>
                                        <Button
                                            variant="outlined"
                                            disableElevation={true}
                                            onClick={() => onHandleMicrosoft(values)}
                                            className="social-button">
                                            <MicrosoftLogo />
                                        </Button>
                                    </Grid>
                                    : ''}
                                <Grid item xs={12}>
                                    <TextField
                                        id="firstName"
                                        value={values.firstName ? values.firstName : ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.firstName && touched.firstName}
                                        helperText={((errors.firstName && touched.firstName) ? <IntlMessages id="register.firstNameRequired" /> : '')}
                                        name="firstName"
                                        inputProps={{ maxLength: 50 }}
                                        fullWidth
                                        required
                                        placeholder={intl.formatMessage({ id: 'register.firstName' })}
                                        className="text-field"
                                        disabled={data.firstFromSocial === true && !!values.firstName} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="lastName"
                                        value={values.lastName ? values.lastName : ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.lastName && touched.lastName}
                                        helperText={((errors.lastName && touched.lastName) ? <IntlMessages id="register.lastNameRequired" /> : '')}
                                        name="lastName"
                                        inputProps={{ maxLength: 50 }}
                                        fullWidth
                                        required
                                        placeholder={intl.formatMessage({ id: 'register.lastName' })}
                                        className="text-field"
                                        disabled={data.lastFromSocial === true && !!values.lastName} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="email"
                                        value={values.email ? values.email : ''}
                                        onChange={emailChanged}
                                        onBlur={emailBlurred}
                                        error={(errors.email && touched.email) || !!values.emailErrorId}
                                        helperText={getEmailHelperText(errors, values)}
                                        name="email"
                                        inputProps={{ maxLength: 50, form: { autocomplete: 'off' } }}
                                        fullWidth
                                        required
                                        placeholder={intl.formatMessage({ id: 'register.email' })}
                                        className="text-field"
                                        disabled={data.emailFromSocial === true} />
                                </Grid>
                                {data.emailFromSocial === false ?
                                    <Grid item xs={12}>
                                        <TextField
                                            id="password"
                                            value={values.newPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.newPassword && touched.newPassword}
                                            FormHelperTextProps={{ component: 'div', margin: 'dense' }}
                                            helperText={errors.newPassword || touched.newPassword ? <PasswordHelperText data={values} /> : ""}
                                            type="password"
                                            name="newPassword"
                                            inputProps={{ maxLength: 256 }}
                                            placeholder={intl.formatMessage({ id: 'register.password' })}
                                            className="text-field"
                                            fullWidth
                                            required />
                                    </Grid>
                                    : ''}
                                {data.emailFromSocial === false ?
                                    <Grid item xs={12}>
                                        <TextField
                                            id="confirmPassword"
                                            value={values.confirmNewPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.confirmNewPassword && touched.confirmNewPassword}
                                            helperText={(errors.confirmNewPassword && touched.confirmNewPassword) ? <IntlMessages id="changePassword.newAndConfirmDoNotMatchShort" /> : ''}
                                            type="password"
                                            name="confirmNewPassword"
                                            inputProps={{ maxLength: 256 }}
                                            placeholder={intl.formatMessage({ id: 'register.confirmPassword' })}
                                            className="text-field"
                                            fullWidth
                                            required />
                                    </Grid>
                                    : ''}
                                {data.registerErrorId
                                    ? <Grid item xs={12}>
                                        <Typography variant="body2" color="error"><IntlMessages id={data.registerErrorId} /></Typography>
                                    </Grid>
                                    : ''}

                                {registerConfig.termsRequiredText ?
                                    <Grid item xs={12} className="register-form-checkbox">
                                    <FormControlLabel
                                            label={<Typography variant="body2"><Link href="#" onClick={(e) => { e.preventDefault(); onShowTerms(values, props); }}><IntlMessages id={registerConfig.termsRequiredText} /></Link></Typography>}
                                        type="checkbox"
                                        control={
                                            <Checkbox
                                                id="agreeToTerms"
                                                value={values.agreeToTerms ? values.agreeToTerms : ''}
                                                onChange={() => onShowTerms(values, props)}
                                                checked={values.agreeToTerms}
                                                helpertext={((errors.agreeToTerms && touched.agreeToTerms) && errors.agreeToTerms)}
                                                error={errors.agreeToTerms && touched.agreeToTerms}
                                                name="agreeToTerms"
                                                className="register-checkbox" />}
                                    />
                                </Grid>
                                    : ''}

                                {registerConfig.receiveEmailText ?
                                <Grid item xs={12} className="register-form-checkbox">
                                    <FormControlLabel
                                        label={<Typography variant="body2" className="login-text" color={touched.emailCommunication && errors.emailCommunication ? 'error' : 'initial'}><IntlMessages id={registerConfig.receiveEmailText} /></Typography>}
                                        type="checkbox"
                                        control={
                                            <Checkbox
                                                id="emailCommunication"
                                                className="register-checkbox"
                                                value={values.emailCommunication ? values.emailCommunication : ''}
                                                onChange={handleChange}
                                                checked={!values.emailCommunication ? false : true}
                                                error={errors.emailCommunication}
                                                indeterminate={values.emailCommunication === null}
                                                name="emailCommunication" />}
                                    />

                                </Grid>
                                : '' }

                                <Grid item xs={12}>
                                    <Button
                                        id="signUp"
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        className="register-button"
                                        fullWidth
                                        disabled={isSubmitting ||
                                            !!errors.firstName ||
                                            !!errors.lastName ||
                                            !!errors.email ||
                                            !!errors.newPassword ||
                                            !!errors.confirmNewPassword ||
                                            values.firstName === '' ||
                                            values.lastName === '' ||
                                            !values.firstName ||
                                            !values.lastName ||
                                            !!values.emailErrorId ||
                                            values.agreeToTerms === false ||
                                            (data.emailFromSocial === false ? (values.newPassword === '' ||
                                                values.confirmNewPassword === '' ||
                                                values.newPassword !== values.confirmNewPassword)
                                                : false)
                                        }>
                                        <IntlMessages id={registerConfig.registerButtonText} />
                                    </Button>
                                </Grid>
                                {registerConfig && registerConfig.loginUrl ?
                                    <Grid item xs={12} className="login-text">
                                        <Grid container alignItems="center" justifyContent="center" direction="column">
                                            <Grid item>
                                                <Typography variant="subtitle1" dangerouslySetInnerHTML={{ __html: signInMessage }} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    : ''}

                            </Grid>
                        </StyledGrid>
                    </div>
                );

            }}
        </FormContainer>
    );
}

export default RegisterWeb;